import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./stage1Heist.css";
import { fireGameStagePopup } from "../../functions/gameStagePopup";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  isGameOver,
  isStageCompletedInSession,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  sumLevelScores,
  LEVELS_BY_ADVENTURE,
} from "../../functions/stageReportSync";
import {
  showStageCompleteCelebration,
  navigateAfterCelebration,
} from "../../functions/stageCelebration";
import {
  applyStageBodyBackground,
  isMobViewport,
} from "./gameStageBackground";
import {
  STAGE1_SCENES,
  STAGE1_DATE_ANSWER,
  STAGE1_LAST_SCENE_INDEX,
} from "./stage1Scenes";
import boardingPassImg from "./img/my-qr-new.png";

const STAGE_NUM = 1;
const GAME_LEVEL = 1;
const CRIME_SVG_URL = `${process.env.PUBLIC_URL || ""}/game/crime.svg`;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${pad2(min)}:${pad2(rem)}`;
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

/** True when stages.gameover (or cached userData.gameover) means play is finished. */
function hasFinishedGame(source) {
  if (!source || typeof source !== "object") return false;
  if (isGameOver(source)) return true;
  const g = source.gameover;
  if (g == null || g === "") return false;
  const n = Number(g);
  return Number.isFinite(n) && n >= 1;
}

function persistUserGameoverFlag(flag = 1) {
  try {
    const raw = sessionStorage.getItem("userData");
    const user = raw ? JSON.parse(raw) : {};
    sessionStorage.setItem(
      "userData",
      JSON.stringify({ ...user, gameover: flag })
    );
  } catch {
    /* ignore */
  }
}

function isImageOption(opt) {
  return typeof opt === "string" && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(opt);
}

/** Drop legacy localStorage progress keys (scene/timer/points) to avoid stale cache. */
function clearLegacyLocalProgress(user) {
  try {
    const uid = user?.userId || user?.userid || user?.id || "anon";
    const sid = user?.sessionId || "session";
    const userKey = `national_heist_${uid}${sid}`;
    localStorage.removeItem(`${userKey}current_scene`);
    localStorage.removeItem(`${userKey}timer`);
    localStorage.removeItem(`${userKey}points`);
  } catch {
    /* ignore */
  }
}

export default function Stage1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const [dbReady, setDbReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [currentScene, setCurrentScene] = useState(0);
  const [points, setPoints] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [dateValue, setDateValue] = useState("");
  const [dateState, setDateState] = useState(""); // "" | "ok" | "bad"
  const [secureClick, setSecureClick] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [hotspotFound, setHotspotFound] = useState(false);
  const [crimeSvgHtml, setCrimeSvgHtml] = useState("");
  const [stackDialsBelow, setStackDialsBelow] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(max-width: 768px) and (orientation: portrait)")
        .matches
    );
  });

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const pointsRef = useRef(0);
  const sceneRef = useRef(0);
  const stageRowIdRef = useRef(stageRowId);

  const sc = STAGE1_SCENES[currentScene] || STAGE1_SCENES[0];
  const isSvgRound = Number(sc.round) === 2;
  const isOptionScene =
    sc.type === "text_optiion" ||
    sc.type === "text_option" ||
    (sc.options?.length > 0 && sc.type !== "input_box" && sc.type !== "next_button");
  const submitLabel = "Next";

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    sceneRef.current = currentScene;
  }, [currentScene]);

  useEffect(() => {
    stageRowIdRef.current = stageRowId;
  }, [stageRowId]);

  useEffect(() => {
    clearLegacyLocalProgress(storedUser);
  }, [storedUser]);

  // Block replay: finished players (gameover=1) always go to leaderboard.
  useEffect(() => {
    const redirectIfFinished = () => {
      const liveUser = getStoredUser();
      if (
        hasFinishedGame(liveUser) ||
        hasFinishedGame(storedUser) ||
        isStageCompletedInSession(STAGE_NUM)
      ) {
        persistUserGameoverFlag(1);
        markStageCompleted(STAGE_NUM, liveUser);
        navigate("/leaderboard", { replace: true });
        return true;
      }
      return false;
    };

    if (redirectIfFinished()) return undefined;

    const onPageShow = (event) => {
      if (event.persisted) redirectIfFinished();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [navigate, storedUser]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(
      "(max-width: 768px) and (orientation: portrait)"
    );
    const sync = () => setStackDialsBelow(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("stage1-escape-page");
    document.documentElement.classList.add("stage1-escape-page");
    const body = document.body;
    const html = document.documentElement;
    const bgKeys = [
      "background-image",
      "background-size",
      "background-position",
      "background-repeat",
      "background-attachment",
      "background-color",
      "min-height",
      "height",
      "overflow",
      "overflow-x",
      "overflow-y",
      "position",
    ];
    const bodySnapshot = bgKeys.map((k) => [k, body.style.getPropertyValue(k)]);
    const htmlSnapshot = ["overflow", "overflow-x", "overflow-y", "height", "min-height"].map(
      (k) => [k, html.style.getPropertyValue(k)]
    );
    const apply = () => applyStageBodyBackground(body, { mob: isMobViewport() });

    // Allow tall scenes (esp. last screen) to scroll on mobile portrait.
    body.style.height = "auto";
    body.style.minHeight = "100dvh";
    body.style.overflowX = "hidden";
    body.style.overflowY = "auto";
    body.style.position = "static";
    html.style.height = "auto";
    html.style.minHeight = "100dvh";
    html.style.overflowX = "hidden";
    html.style.overflowY = "auto";

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      bodySnapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
      htmlSnapshot.forEach(([k, v]) => {
        if (v) html.style.setProperty(k, v);
        else html.style.removeProperty(k);
      });
      document.body.classList.remove("stage1-escape-page");
      document.documentElement.classList.remove("stage1-escape-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(CRIME_SVG_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`crime.svg HTTP ${r.status}`);
        return r.text();
      })
      .then((html) => {
        if (!cancelled) setCrimeSvgHtml(html);
      })
      .catch((err) => console.error("crime.svg load:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    function applyResumedElapsed(seconds) {
      const sec = Math.max(0, Math.floor(Number(seconds) || 0));
      baseElapsedRef.current = sec;
      elapsedRef.current = sec;
      if (timerStartedRef.current) {
        sessionStartRef.current = Date.now();
      }
      if (!cancelled) setElapsedSec(sec);
    }

    async function loadStageFromDb() {
      const liveUser = getStoredUser();
      if (
        hasFinishedGame(liveUser) ||
        hasFinishedGame(storedUser) ||
        isStageCompletedInSession(STAGE_NUM)
      ) {
        persistUserGameoverFlag(1);
        markStageCompleted(STAGE_NUM, liveUser);
        if (!cancelled) navigate("/leaderboard", { replace: true });
        return;
      }

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: storedUser.name || "",
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (rowId && !cancelled) {
          setStageRowId(rowId);
          stageRowIdRef.current = rowId;
        }

        if (rowId) {
          const stageRes = await fetch(`${backendBase}/getGameStage`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId: rowId,
            }),
          });
          const stageData = await stageRes.json().catch(() => ({}));
          if (cancelled) return;

          if (stageRes.ok && stageData.success && stageData.stage) {
            const st = stageData.stage;
            hydrateCompletedStagesFromRecord(st);

            if (hasFinishedGame(st)) {
              persistUserGameoverFlag(1);
              markStageCompleted(STAGE_NUM, storedUser);
              navigate("/leaderboard", { replace: true });
              return;
            }

            const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
            if (resumeRoute) {
              if (resumeRoute === "/leaderboard") persistUserGameoverFlag(1);
              navigate(resumeRoute, { replace: true });
              return;
            }

            const dbTimeSec = parseClockToSeconds(st.time);
            const dbTotal = Number(st.total_score) || 0;
            const dbStage1Sum = sumLevelScores(
              st,
              LEVELS_BY_ADVENTURE[STAGE_NUM]
            );
            const dbScene = Math.max(
              0,
              Math.min(
                STAGE1_LAST_SCENE_INDEX,
                Math.floor(Number(st.currentQues) || 0)
              )
            );

            applyResumedElapsed(dbTimeSec);
            otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1Sum);
            if (!cancelled) {
              setPoints(dbStage1Sum);
              pointsRef.current = dbStage1Sum;
              setCurrentScene(dbScene);
              sceneRef.current = dbScene;
            }
            setStageActive(STAGE_NUM, storedUser);
          } else {
            setStageActive(STAGE_NUM, storedUser);
          }
        } else {
          // No stage row id yet — still check fetchReport for gameover.
          try {
            const reportRes = await fetch(`${backendBase}/fetchReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({ userId: uid, sessionId, organizationId }),
            });
            const reportData = await reportRes.json().catch(() => ({}));
            if (
              reportRes.ok &&
              reportData?.success &&
              hasFinishedGame(reportData.report)
            ) {
              persistUserGameoverFlag(1);
              markStageCompleted(STAGE_NUM, storedUser);
              if (!cancelled) navigate("/leaderboard", { replace: true });
              return;
            }
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error("Stage1 loadStageFromDb:", err);
      }

      if (!cancelled) setDbReady(true);
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  useEffect(() => {
    setSelectedOption(null);
    setDateValue("");
    setDateState("");
    setShowQrModal(false);
    setHotspotFound(false);
    setSecureClick(false);
    const t = setTimeout(() => setSecureClick(true), 800);
    return () => clearTimeout(t);
  }, [currentScene]);

  const persistProgressToDb = useCallback(
    async (sceneIdx, pts, { syncReport = false } = {}) => {
      if (isDemoBypass || !backendBase || !adminToken) return;
      const stageId = stageRowIdRef.current || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId) return;

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const totalScore = otherStagesTotalRef.current + pts;

      try {
        await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: GAME_LEVEL,
          score: pts,
          answers: {
            level: GAME_LEVEL,
            puzzle: "national_heist",
            points: pts,
            scene: sceneIdx,
          },
          time: timeStr,
          total_score: totalScore,
          currentQues: sceneIdx,
          syncReport,
        });
      } catch (err) {
        console.error("Stage1 persistProgressToDb:", err);
      }
    },
    [adminToken, backendBase, isDemoBypass, storedUser]
  );

  // Keep DB time fresh if the tab is hidden / refreshed mid-scene.
  useEffect(() => {
    if (!dbReady || isDemoBypass) return;

    const flush = () => {
      if (submittedRef.current) return;
      void persistProgressToDb(sceneRef.current, pointsRef.current, {
        syncReport: false,
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dbReady, isDemoBypass, persistProgressToDb]);

  const finishStage = useCallback(
    async (finalPoints) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowIdRef.current || sessionStorage.getItem(STAGE_ROW_KEY);
      const totalScore = otherStagesTotalRef.current + finalPoints;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        try {
          const uid = storedUser.userId || storedUser.userid || storedUser.id;
          const sessionId = storedUser.sessionId;
          const organizationId = storedUser.organizationId;

          await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: GAME_LEVEL,
            score: finalPoints,
            answers: {
              level: GAME_LEVEL,
              puzzle: "national_heist",
              points: finalPoints,
            },
            time: timeStr,
            total_score: totalScore,
            currentQues: STAGE1_LAST_SCENE_INDEX,
            syncReport: true,
          });

          await fetch(`${backendBase}/welcomeStageComplete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId,
              time: timeStr,
              total_score: totalScore,
              points: totalScore,
            }),
          });
        } catch (err) {
          console.error("Stage1 finish save:", err);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      persistUserGameoverFlag(1);
      clearLegacyLocalProgress(storedUser);

      if (isDemoBypass) {
        await fireGameStagePopup({
          title: "Subscribe to play",
          html: "<p>Subscribe to any PLAN to play with your peers.</p>",
          icon: "info",
          confirmButtonText: "OK",
          themeData,
        });
        window.location.assign("https://extramileplay.com/plans");
        return;
      }

      await showStageCompleteCelebration({
        skip: false,
        score: finalPoints,
      });
      navigateAfterCelebration(navigate, "/leaderboard");
    },
    [
      submitting,
      isDemoBypass,
      backendBase,
      adminToken,
      storedUser,
      themeData,
      navigate,
    ]
  );

  const goNext = useCallback(async () => {
    if (!secureClick) return;
    setSecureClick(false);

    if (isDemoBypass) {
      await finishStage(pointsRef.current);
      return;
    }

    const idx = sceneRef.current;
    const sceneData = STAGE1_SCENES[idx];

    if (idx >= STAGE1_LAST_SCENE_INDEX) {
      const finalPts = pointsRef.current + (sceneData?.count_points || 0);
      setPoints(finalPts);
      await finishStage(finalPts);
      return;
    }

    const nextPts = pointsRef.current + (sceneData?.count_points || 0);
    const nextIdx = idx + 1;
    setPoints(nextPts);
    pointsRef.current = nextPts;
    setCurrentScene(nextIdx);
    sceneRef.current = nextIdx;
    await persistProgressToDb(nextIdx, nextPts, { syncReport: false });
    setTimeout(() => setSecureClick(true), 800);
  }, [secureClick, isDemoBypass, finishStage, persistProgressToDb]);

  const handleSubmit = useCallback(async () => {
    if (!secureClick || submitting) return;
    const sceneData = STAGE1_SCENES[sceneRef.current];

    if (sceneData.type === "next_button") {
      if (Number(sceneData.round) === 2 && !hotspotFound) {
        await fireGameStagePopup({
          title: "Keep looking",
          html: "<p>Find the hidden hotspot on the map first.</p>",
          icon: "info",
          confirmButtonText: "OK",
          themeData,
        });
        return;
      }
      await goNext();
      return;
    }

    const isQuiz =
      sceneData.type === "text_optiion" ||
      sceneData.type === "text_option" ||
      (sceneData.options?.length > 0 && sceneData.type !== "input_box");

    if (isQuiz) {
      if (selectedOption == null) {
        await fireGameStagePopup({
          title: "Please Select any option",
          html: "",
          icon: "error",
          confirmButtonText: "OK",
          themeData,
        });
        return;
      }
      if (selectedOption !== sceneData.answer_id) {
        await fireGameStagePopup({
          title: "Wrong Answer",
          html: "",
          icon: "error",
          confirmButtonText: "OK",
          themeData,
        });
        return;
      }
      await goNext();
      return;
    }

    if (sceneData.type === "input_box") {
      if (dateValue === STAGE1_DATE_ANSWER || dateValue === sceneData.answer_id) {
        await goNext();
      } else {
        await fireGameStagePopup({
          title: "Wrong Answer",
          html: "",
          icon: "error",
          confirmButtonText: "OK",
          themeData,
        });
      }
    }
  }, [
    secureClick,
    submitting,
    hotspotFound,
    selectedOption,
    dateValue,
    goNext,
    themeData,
  ]);

  const handleDateChange = (e) => {
    const v = e.target.value;
    setDateValue(v);
    if (!v) {
      setDateState("");
      return;
    }
    setDateState(v === STAGE1_DATE_ANSWER ? "ok" : "bad");
  };

  const handleCrimeClick = (e) => {
    const el = e.target.closest?.(".five, .error");
    if (!el) return;
    if (el.classList.contains("five")) {
      setHotspotFound(true);
      setShowQrModal(true);
    } else {
      fireGameStagePopup({
        title: "Try Again",
        html: "",
        icon: "error",
        confirmButtonText: "OK",
        themeData,
      });
    }
  };

  if (!dbReady) {
    return (
      <div className="heist-stage">
        <p className="heist-stage__loading">Loading game…</p>
      </div>
    );
  }

  const options = sc.options || [];
  const showOptions = isOptionScene && options.length > 0;
  const showInput = sc.type === "input_box";
  const canShowSubmitButton = isSvgRound ? hotspotFound : true;
  const showDials = sc.dial?.length > 0;

  return (
    <div className="heist-stage">
      <div className="heist-timer" aria-live="polite">
        Time: <span>{formatSecondsToClock(elapsedSec)}</span>
      </div>

      <div className="heist-scene">
        {!isSvgRound ? (
          <img
            src={sc.scene_image}
            alt=""
            className="heist-scene__img"
            draggable={false}
          />
        ) : null}

        {isSvgRound ? (
          crimeSvgHtml ? (
            <div
              className="heist-crime-svg"
              onClick={handleCrimeClick}
              dangerouslySetInnerHTML={{ __html: crimeSvgHtml }}
            />
          ) : (
            <p className="heist-stage__loading">Loading crime scene…</p>
          )
        ) : null}

        {sc.show_led ? (
          <div className="heist-led" aria-hidden>
            <span className="heist-led__bar" />
          </div>
        ) : null}

        {showDials ? (
          <div
            className={`heist-dial-handler${stackDialsBelow ? " heist-dial-handler--stacked" : ""}`}
          >
            {sc.dial.map((text, i) => (
              <div
                key={`${currentScene}-dial-${i}`}
                className="heist-comment"
                style={
                  stackDialsBelow
                    ? undefined
                    : parseInlineStyle(sc.dial_cords?.[i])
                }
              >
                “{text}”
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {sc.info ? <div className="heist-info">{sc.info}</div> : null}

      {showOptions ? (
        <div className="heist-options">
          {options.map((opt, i) => {
            const asImage = sc.has_option_image || isImageOption(opt);
            const selected = selectedOption === i;
            return (
              <button
                key={`opt-${i}`}
                type="button"
                className={`heist-option${asImage ? " heist-option--image" : ""}${selected ? " heist-option--selected" : ""}`}
                onClick={() => setSelectedOption(i)}
              >
                {asImage ? (
                  <img src={opt} alt="" className="heist-option__img" />
                ) : (
                  opt
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      {showInput ? (
        <div className="heist-input-wrap">
          <input
            className={`heist-input${
              dateState === "ok"
                ? " heist-input--ok"
                : dateState === "bad"
                  ? " heist-input--bad"
                  : ""
            }`}
            type="date"
            value={dateValue}
            onChange={handleDateChange}
            readOnly={dateState === "ok"}
            aria-label="Enter date"
          />
        </div>
      ) : null}

      {canShowSubmitButton ? (
        <div className="heist-submit-wrap">
          <button
            type="button"
            className="heist-submit"
            onClick={handleSubmit}
            disabled={submitting || !secureClick}
          >
            {submitting ? "Loading…" : submitLabel}
          </button>
        </div>
      ) : null}

      {showQrModal ? (
        <div className="heist-modal-overlay" role="dialog" aria-modal="true">
          <div className="heist-modal">
            <div className="heist-modal__header">
              <h3 className="heist-modal__title">Boarding Pass Recovered</h3>
            </div>
            <div className="heist-modal__body">
              <img src={boardingPassImg} alt="Boarding pass with QR code" />
            </div>
            <div className="heist-modal__footer">
              <button
                type="button"
                className="heist-modal__close"
                onClick={() => setShowQrModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseInlineStyle(cssText) {
  if (!cssText || typeof cssText !== "string") return undefined;
  const style = {};
  cssText.split(";").forEach((part) => {
    const [rawKey, ...rest] = part.split(":");
    if (!rawKey || rest.length === 0) return;
    const key = rawKey
      .trim()
      .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    style[key] = rest.join(":").trim();
  });
  return style;
}
